import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { Blister, Maybe, PointOfInterest, TemplateField } from '@/__generated__/graphql';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import { useAtomValue } from 'jotai';
import { ControlledFieldAssignment } from '@/constants';

export type TemplateDataHookKeys = { [key: string]: string };

type Action =
  | { type: 'UPDATE_FIELD'; payload: { key: string; value: string } }
  | { type: 'REMOVE_FIELD'; payload: { key: string; value: '' } }
  | { type: 'ADD_FIELD'; payload: { key: string; value: string } }
  | { type: 'RESET_STATE'; payload: TemplateDataHookKeys };

const reducer = (state: TemplateDataHookKeys, action: Action) => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return { ...state, [action.payload.key]: action.payload.value };
    case 'REMOVE_FIELD':
      return { ...state, [action.payload.key]: action.payload.value };
    case 'ADD_FIELD':
      return { ...state, [action.payload.key]: action.payload.value };
    case 'RESET_STATE':
      return action.payload;
    default:
      return state;
  }
};

export type UseTemplateFieldsHookType = {
  type: string;
  blister: Maybe<Blister> | undefined;
  state: TemplateDataHookKeys;
  handleChange: (key: string, value: string) => void;
  handleRemove: (key: string) => void;
  handleAdd: (key: string, value: string) => void;
  getTemplateFieldDataInput: () => Record<string, string | number | undefined> | undefined;
};

type Props = {
  templateFields: TemplateField[] | undefined;
  selectedPoiInfo: Partial<PointOfInterest> | undefined;
  fieldType?: 'value' | 'verifiedValue';
};

export const useTemplateFields = ({
  templateFields,
  selectedPoiInfo,
  fieldType = 'value',
}: Props): UseTemplateFieldsHookType => {
  const isUserInteracting = useRef(false);
  const defectsList = useAtomValue(poiState.defectsList);
  const initialState = useMemo(() => {
    const result = (templateFields || [])
      .filter((item) => !item.isReadOnly)
      .reduce((accumulator, templateField) => {
        if (accumulator[templateField?.key ?? '']) return accumulator;
        // Handle templateFieldData as an object instead of an array
        // For verified values, check for both regular key and verified_key prefix
        const templateFieldValue =
          templateField.key && selectedPoiInfo?.templateFieldData
            ? fieldType === 'verifiedValue'
              ? selectedPoiInfo.templateFieldData[`verified_${templateField.key}`] ||
                selectedPoiInfo.templateFieldData[templateField.key]
              : selectedPoiInfo.templateFieldData[templateField.key]
            : undefined;
        // Fall back to the old templateData array if needed
        const templateData = selectedPoiInfo?.templateData?.find(
          (td) => td.id === templateField.id
        );
        if (defectsList.some((defect) => defect.key === templateField.key)) {
          const defectValue =
            defectsList.find((defect) => defect.key === templateField.key)?.value ?? '';

          if (templateField.detailFieldKey) {
            const detailField = templateFields?.find(
              (field) => field.key === templateField.detailFieldKey
            );
            if (detailField) {
              return {
                ...accumulator,
                [templateField?.key ?? '']: defectValue,
                [detailField?.key ?? '']: ControlledFieldAssignment.AUTO_GENERATED,
              };
            }
          }

          return {
            ...accumulator,
            [templateField?.key ?? '']: defectValue,
          };
        }

        // First try to get value from templateFieldData, then fall back to templateData
        // Use templateFieldValue if it exists (it's already been determined based on fieldType above)
        // Otherwise fall back to templateData array
        const finalValue =
          templateFieldValue !== undefined && templateFieldValue !== null
            ? templateFieldValue
            : (templateData?.[fieldType] ?? templateData?.value ?? '');
        return {
          ...accumulator,
          [templateField?.key ?? '']: finalValue,
        };
      }, {} as TemplateDataHookKeys);
    return result;
  }, [
    defectsList,
    fieldType,
    selectedPoiInfo?.templateData,
    selectedPoiInfo?.templateFieldData,
    templateFields,
  ]);

  const [state, dispatch] = useReducer(reducer, initialState);

  // Track the previous initialState keys to detect when we switch to a different POI/template
  const previousInitialStateKeysRef = useRef<string[]>([]);

  useEffect(() => {
    const currentInitialStateKeys = Object.keys(initialState);
    const previousInitialStateKeys = previousInitialStateKeysRef.current;
    // Only reset isUserInteracting when the initialState keys change (e.g., switching POI/template)
    // NOT when state keys change due to programmatic updates like SC category
    if (JSON.stringify(previousInitialStateKeys) !== JSON.stringify(currentInitialStateKeys)) {
      isUserInteracting.current = false;
      previousInitialStateKeysRef.current = currentInitialStateKeys;
    }

    if (JSON.stringify(initialState) !== JSON.stringify(state) && !isUserInteracting.current) {
      dispatch({
        type: 'RESET_STATE',
        payload: fieldType === 'verifiedValue' ? state : initialState,
      });
    }
  }, [fieldType, initialState, state, templateFields]);

  const handleChange = useCallback((key: string, value: string) => {
    isUserInteracting.current = true;
    dispatch({ type: 'UPDATE_FIELD', payload: { key, value } });
  }, []);

  const handleRemove = useCallback((key: string) => {
    isUserInteracting.current = true;
    dispatch({ type: 'REMOVE_FIELD', payload: { key, value: '' } });
  }, []);

  const handleAdd = useCallback((key: string, value: string) => {
    isUserInteracting.current = true;
    dispatch({ type: 'ADD_FIELD', payload: { key, value } });
  }, []);

  const getTemplateFieldDataInput = useCallback(() => {
    const result: Record<string, string | number | undefined> = {};
    for (const [key, value] of Object.entries(state)) {
      const found = templateFields?.find((field) => field.key === key);
      if (found?.key) {
        result[found.key] = value;
      }
    }
    const areEqual = Object.keys(result).length === Object.keys(state).length;

    return areEqual ? result : undefined;
  }, [state, templateFields]);

  return {
    state,
    handleChange,
    handleRemove,
    handleAdd,
    type: selectedPoiInfo?.type ?? '',
    blister: selectedPoiInfo?.blister,
    getTemplateFieldDataInput,
  };
};
